import { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useNavigate } from "react-router-dom";

import { Typography, Stack, Box, Button } from "@mui/material";
import {
  PresaleInfo,
  defaultPresaleInfo,
  getPresaleName,
  formatFulfillments,
} from "Model/PresaleInfo";
import { initializeCart } from "Redux/actionCreators";
import { useAppDispatch, useAppSelector } from "Redux/hooks";
import { fetchPresale } from "Util/firebaseHelper";
import { selectPresaleById } from "Redux/selectors";

type Props = {
  editing: boolean;
  presaleId: string;
  username: string;
};

export const PresaleEntry = ({ editing, presaleId, username }: Props) => {
  const navigate = useNavigate();
  const presale =
    useAppSelector((state) => selectPresaleById(state, presaleId)) ||
    defaultPresaleInfo(false, []);
  const [presaleInfo, setPresaleInfo] = useState<PresaleInfo>(
    defaultPresaleInfo(false, [])
  );
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      if (presaleId) {
        const presale = await fetchPresale(presaleId);
        if (presale === null) {
        } else {
          console.log(presale.data());
          let content = presale.data();
          if (content) {
            const presaleInfo = content as PresaleInfo;
            setPresaleInfo(presaleInfo);
            dispatch(initializeCart(presaleInfo.presaleItems));
          }
        }
      }
      setLoading(false);
    }
    if (!editing) {
      fetchData();
    }
  }, []);

  const theme = useTheme();
  const isMdScreen = useMediaQuery(theme.breakpoints.up("md"));
  const previewImgCount = isMdScreen ? 3 : 3;

  const renderOverlappingImages = (items: any[]) => {
    const imagesToShow = items
      .filter((i) => i.img && !i.sensitive)
      .slice(0, previewImgCount);
    const remainingCount = items.filter((i) => i.img).length - previewImgCount;

    if (imagesToShow.length === 0) return null;

    // Single image
    if (imagesToShow.length === 1) {
      return (
        <Box sx={{ position: "relative", width: "100%", height: 200, mt: 1 }}>
          <Box
            sx={{
              position: "absolute",
              width: "100%",
              height: "100%",
              borderRadius: 4,
              overflow: "hidden",
            }}
          >
            <img
              src={imagesToShow[0].img}
              alt="item"
              loading="lazy"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </Box>
        </Box>
      );
    }

    // Two images
    if (imagesToShow.length === 2) {
      return (
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: 200,
            mt: 1,
            mb: 1,
          }}
        >
          {/* 1st image - bottom left */}
          <Box
            sx={{
              position: "absolute",
              bottom: "5%",
              left: "8%",
              height: "85%",
              aspectRatio: "5 / 4",
              borderRadius: 4,
              border: "2px solid white",
              overflow: "hidden",
              boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
              transform: "rotate(-8deg)",
              zIndex: 1,
            }}
          >
            <img
              src={imagesToShow[0].img}
              alt="item"
              loading="lazy"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </Box>
          {/* 2nd image - top right */}
          <Box
            sx={{
              position: "absolute",
              top: "5%",
              right: "8%",
              height: "85%",
              aspectRatio: "5 / 4",
              borderRadius: 4,
              border: "2px solid white",
              overflow: "hidden",
              boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
              transform: "rotate(5deg)",
              zIndex: 2,
            }}
          >
            <img
              src={imagesToShow[1].img}
              alt="item"
              loading="lazy"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </Box>
        </Box>
      );
    }

    // Three or more images
    return (
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: 200,
          mt: 1,
          mb: 1,
        }}
      >
        {/* 1st image - bottom left */}
        <Box
          sx={{
            position: "absolute",
            bottom: "5%",
            height: "85%",
            aspectRatio: "5 / 4",
            left: "2%",
            borderRadius: 4,
            border: "2px solid white",
            overflow: "hidden",
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
            transform: "rotate(-8deg)",
            zIndex: 1,
          }}
        >
          <img
            src={imagesToShow[0].img}
            alt="item"
            loading="lazy"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </Box>

        {/* 2nd image - top right */}
        <Box
          sx={{
            position: "absolute",
            bottom: "5%",
            height: "85%",
            aspectRatio: "5 / 4",
            right: "2%",
            borderRadius: 4,
            border: "2px solid white",
            overflow: "hidden",
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
            transform: "rotate(8deg)",
            zIndex: 2,
          }}
        >
          <img
            src={imagesToShow[1].img}
            alt="item"
            loading="lazy"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </Box>

        {/* 3rd image - center */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            height: "95%",
            aspectRatio: "5 / 4",
            left: "50%",
            transform: "translateX(-50%)",
            borderRadius: 4,
            border: "2px solid white",
            overflow: "hidden",
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
            zIndex: 3,
          }}
        >
          <img
            src={imagesToShow[2].img}
            alt="item"
            loading="lazy"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </Box>
        {remainingCount > 0 && (
          <Box
            sx={{
              position: "absolute",
              width: 32,
              height: 32,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "white",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              borderRadius: "50%",
              zIndex: 4,
            }}
          >
            <Typography variant="subtitle2">+{remainingCount}</Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Stack
      onClick={() => {
        if (editing) {
          navigate(`/admin/presales/${presaleId}#orders`);
        } else {
          navigate(`/${username}/presale/${presaleId}`);
        }
      }}
      sx={{
        backgroundColor: "white",
        borderRadius: 5,
        p: { xs: 2, sm: 3 },
        cursor: "pointer",
      }}
    >
      {editing ? (
        <Stack>
          <Typography
            variant="h6"
            flex={1}
            sx={{
              fontFamily: "Lora",
              fontSize: "1.3rem",
            }}
          >
            {getPresaleName(presale.presaleName)}
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={1}>
            Pickup on {formatFulfillments(presale.fulfillments)} • Limited time
          </Typography>
          {renderOverlappingImages(presale.presaleItems)}
        </Stack>
      ) : (
        <Stack
          spacing={2}
          flexDirection={
            presaleInfo.presaleItems.filter((i) => i.img && !i.sensitive)
              .length === 0
              ? "row"
              : "column"
          }
        >
          <Stack flex={1}>
            <Typography
              variant="h6"
              flex={1}
              sx={{
                fontFamily: "Lora",
                fontSize: "1.3rem",
              }}
            >
              {getPresaleName(presaleInfo.presaleName)}
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={1}>
              Pickup on {formatFulfillments(presaleInfo.fulfillments)} • Limited
              time
            </Typography>
            {renderOverlappingImages(presaleInfo.presaleItems)}
          </Stack>
          <Stack alignItems="center">
            <Button variant="contained">Order now</Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
